import React, { useState, useEffect } from "react";
import { NavLink, useLocation } from "react-router-dom";
import styled, { keyframes, css } from "styled-components";
import { FaBars, FaTimes } from "react-icons/fa";
import TripproLogo from "../assests/tripproLogo.png";

// --- Animations ---
const slideDown = keyframes`
  from { opacity: 0; transform: translateY(-20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const underlineExpand = keyframes`
  from { width: 0; }
  to { width: 100%; }
`;

// --- Styled Components ---

const NavbarContainer = styled.nav`
  position: fixed;
  top: 0;
  width: 100%;
  /* Glassmorphism Effect */
  background: rgba(233, 246, 249, 0.95); /* Slightly more opaque for better readability */
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  
  color: #004e61;
  z-index: 1000;
  display: flex;
  justify-content: center; /* Center the inner content */
  height: 80px; /* Increased height for better spacing */
  transition: all 0.3s ease;
  box-shadow: ${props => props.$scrolled ? '0 4px 6px -1px rgba(0, 78, 97, 0.1)' : 'none'};
`;

const NavContent = styled.div`
  width: 100%;
  max-width: 1400px; /* Constrain content width */
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 2rem;
  height: 100%;

  @media (max-width: 768px) {
    padding: 0 1.5rem;
  }
`;

const LeftMenu = styled.div`
  flex: 1;
  display: flex;
  justify-content: flex-start;

  @media (max-width: 900px) { /* Increased breakpoint */
    display: none;
  }
`;

const CenterLogo = styled.div`
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    width: 160px;
    height: auto;
    transition: width 0.3s ease;
  }

  @media (max-width: 900px) { /* Match breakpoint */
    position: static;
    transform: none;
    flex: 1;
    justify-content: flex-start;
    
    img {
      width: 140px;
    }
  }
`;

const RightMenu = styled.div`
  flex: 1;
  display: flex;
  justify-content: flex-end;

  @media (max-width: 900px) { /* Increased breakpoint */
    display: none;
  }
`;

const NavList = styled.ul`
  display: flex;
  list-style: none;
  gap: 2rem; /* Reduced gap slightly to prevent early collisions */
  align-items: center;
  margin: 0;
  padding: 0;
`;

const NavItem = styled.li`
  position: relative;
`;

const StyledNavLink = styled(NavLink)`
  text-decoration: none;
  color: #004e61;
  font-family: "Merriweather", serif;
  font-size: 16px; 
  font-weight: 600; /* Slightly bolder for readability */
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 5px 0;
  position: relative;
  transition: color 0.3s ease;

  /* Underline Animation */
  &::after {
    content: '';
    position: absolute;
    width: 0;
    height: 2px;
    bottom: 0;
    left: 0;
    background-color: #1CA8CB;
    transition: width 0.3s ease-in-out;
  }

  &:hover {
    color: #1CA8CB;
  }

  &:hover::after {
    width: 100%;
  }

  &.active {
    color: #1CA8CB;
    &::after {
      width: 100%;
    }
  }
`;

const HamburgerIcon = styled.div`
  display: none;
  font-size: 1.8rem;
  cursor: pointer;
  color: #004e61;
  z-index: 1001;
  transition: transform 0.2s ease;

  &:active {
    transform: scale(0.9);
  }

  @media (max-width: 900px) { /* Show sooner */
    display: block;
    margin-left: auto; 
  }
`;

const MobileMenuOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100vh;
  background: rgba(0, 0, 0, 0.4);
  backdrop-filter: blur(4px);
  z-index: 998;
  opacity: ${props => (props.$isOpen ? 1 : 0)};
  visibility: ${props => (props.$isOpen ? "visible" : "hidden")};
  transition: all 0.3s ease-in-out;
`;

const MobileMenu = styled.ul`
  display: flex;
  flex-direction: column;
  list-style: none;
  background: rgba(255, 255, 255, 0.98);
  backdrop-filter: blur(10px);
  position: fixed;
  top: 0;
  right: 0;
  width: 300px;
  height: 100vh;
  padding: 6rem 2rem 2rem; 
  margin: 0;
  box-shadow: -5px 0 25px rgba(0, 0, 0, 0.15);
  z-index: 999;
  transform: ${props => (props.$isOpen ? "translateX(0)" : "translateX(100%)")};
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
`;

const MobileMenuItem = styled.li`
  margin-bottom: 2rem;
  opacity: ${props => (props.$isOpen ? 1 : 0)};
  transform: ${props => (props.$isOpen ? "translateX(0)" : "translateX(20px)")};
  transition: all 0.3s ease;
  transition-delay: ${props => props.$index * 0.1}s;

  &:last-child {
    margin-bottom: 0;
  }
`;

const MobileNavLink = styled(StyledNavLink)`
  font-size: 20px;
  display: block;
  padding: 10px 0;
  border-bottom: 1px solid rgba(0, 78, 97, 0.1);
  
  &:hover {
    padding-left: 10px; 
    color: #1CA8CB;
  }

  &.active {
    border-bottom: 2px solid #1CA8CB;
  }
`;

const CloseButton = styled.div`
  position: absolute;
  top: 25px;
  right: 25px;
  font-size: 2rem;
  color: #004e61;
  cursor: pointer;
  
  &:hover {
    color: #1CA8CB;
  }
`;

const Navbar = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  // Handle Scroll Effect
  useEffect(() => {
    const handleScroll = () => {
      const offset = window.scrollY;
      if (offset > 50) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location]);

  const toggleMobileMenu = () => {
    setMobileMenuOpen((prev) => !prev);
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  const menuItems = [
    { name: "Home", path: "/" },
    { name: "About Us", path: "/about" },
    { name: "Blog", path: "/blog" },
    { name: "Contact Us", path: "/contact" },
    { name: "Career", path: "/career" },
    { name: "My Trips", path: "/my-trips" },
  ];

  return (
    <NavbarContainer $scrolled={scrolled}>
      {/* Content Wrapper to constrain width */}
      <NavContent>
        {/* Left desktop menu */}
        <LeftMenu>
          <NavList>
            <NavItem>
              <StyledNavLink to="/">Home</StyledNavLink>
            </NavItem>
            <NavItem>
              <StyledNavLink to="/about">About Us</StyledNavLink>
            </NavItem>
          </NavList>
        </LeftMenu>

        {/* Center logo (Acts as Home Link) */}
        <CenterLogo>
          <NavLink to="/" onClick={closeMobileMenu}>
            <img src={TripproLogo} alt="TripPro Logo" />
          </NavLink>
        </CenterLogo>

        {/* Right desktop menu */}
        <RightMenu>
          <NavList>
            <NavItem>
              <StyledNavLink to="/blog">Blog</StyledNavLink>
            </NavItem>
            <NavItem>
              <StyledNavLink to="/contact">Contact Us</StyledNavLink>
            </NavItem>
            <NavItem>
              <StyledNavLink to="/career">Career</StyledNavLink>
            </NavItem>
            <NavItem>
              <StyledNavLink to="/my-trips">My Trips</StyledNavLink>
            </NavItem>
          </NavList>
        </RightMenu>

        {/* Hamburger icon for mobile */}
        <HamburgerIcon onClick={toggleMobileMenu}>
          <FaBars />
        </HamburgerIcon>
      </NavContent>

      {/* Mobile Menu Overlay for checking clicks outside */}
      <MobileMenuOverlay $isOpen={mobileMenuOpen} onClick={closeMobileMenu} />

      {/* Mobile menu Sidebar */}
      <MobileMenu $isOpen={mobileMenuOpen}>
        <CloseButton onClick={closeMobileMenu}>
          <FaTimes />
        </CloseButton>
        {menuItems.map((item, index) => (
          <MobileMenuItem key={item.name} $index={index} $isOpen={mobileMenuOpen}>
            <MobileNavLink to={item.path} onClick={closeMobileMenu}>
              {item.name}
            </MobileNavLink>
          </MobileMenuItem>
        ))}
      </MobileMenu>
    </NavbarContainer>
  );
};

export default Navbar;